import { type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import type { User } from '@reflex/shared';
import { SearchSelect, type SearchSelectOption } from '@reflex/ui/components/shared/SearchSelect';
import { UserAvatar } from '@/components/shared/UserAvatar';

interface MemberPickerProps {
  users: User[];
  onSelect: (userId: string) => void;
  disabled?: boolean;
  /**
   * Visible label for the trigger button. Defaults to "Add member".
   */
  triggerLabel?: string;
  /**
   * Stable accessible name for the trigger. Defaults to "Add member" so play
   * functions can locate it independently of the visible copy.
   */
  ariaLabel?: string;
  /**
   * Placeholder text for the search input.
   */
  searchPlaceholder?: string;
  /**
   * Content shown inside the popover when `users` is empty. Lets callers
   * explain *why* there is nothing to pick (e.g. "Invite people first" with
   * a link). The trigger stays enabled so the user can read the explanation.
   */
  emptyState?: ReactNode;
}

/**
 * Picker that lets the caller choose an existing user from a provided list,
 * typically users not yet members of an organization or team. Pure
 * presentational component — the caller is responsible for filtering the list
 * and reacting to selection.
 *
 * When the provided `users` list is empty the trigger remains enabled and
 * opening the popover surfaces the caller-provided `emptyState` so the user
 * gets an explanation instead of a silently-disabled button. Built on the
 * shared {@link SearchSelect} chrome.
 */
export function MemberPicker({
  users,
  onSelect,
  disabled,
  triggerLabel = 'Add member',
  ariaLabel = 'Add member',
  searchPlaceholder = 'Search members...',
  emptyState = 'No users available to add.',
}: MemberPickerProps) {
  const isEmpty = users.length === 0;
  const options: SearchSelectOption<string>[] = users.map((user) => {
    const display = user.name ?? user.login;
    return {
      key: user.id,
      value: user.id,
      label: (
        <span className="flex min-w-0 items-center gap-2">
          <UserAvatar userName={display} avatarUrl={user.avatarUrl ?? undefined} size="xs" />
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-sm">{display}</span>
            <span className="truncate text-xs text-muted-foreground">@{user.login}</span>
          </span>
        </span>
      ),
      searchValue: `${user.login} ${user.name ?? ''} ${user.email}`,
      'data-testid': `member-picker-option-${user.id}`,
    };
  });

  return (
    <SearchSelect
      ariaLabel={ariaLabel}
      triggerLabel={
        <span className="inline-flex items-center gap-1.5">
          <Plus className="icon-sm" />
          {triggerLabel}
        </span>
      }
      value=""
      onChange={onSelect}
      options={options}
      disabled={disabled}
      searchPlaceholder={searchPlaceholder}
      emptyMessage="No matching users."
      contentAlign="end"
      className="w-fit"
      contentClassName="w-72"
      overrideBody={
        isEmpty ? (
          <div
            className="px-3 py-6 text-center text-sm text-muted-foreground"
            data-testid="member-picker-empty-state"
          >
            {emptyState}
          </div>
        ) : undefined
      }
    />
  );
}
